import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography, Avatar } from '@mui/material';
import { signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { client } from '../../api/graphql';
import {
  INVITE_PREVIEW,
  REDEEM_INVITE_AUTOLOGIN,
  REDEEM_INVITE_FOR_CURRENT_USER,
} from '../../api/graphql_invite';

// Flip APP_ON_APP_STORE to true once the app is live on the public App
// Store; until then we onboard testers via TestFlight.
const APP_ON_APP_STORE = false;
const APP_STORE_URL = 'https://apps.apple.com/app/id6772217916';
// Public TestFlight join link — set this to the real one from App Store
// Connect (TestFlight → Public Link). Empty string → show manual steps.
const TESTFLIGHT_URL = 'https://testflight.apple.com/join/j7GVVMRG';


// Natural join of first names: "Noor", "Noor & Yusuf", "Noor, Yusuf & Zayd".
function joinNames(names: string[]): string {
  const first = names
    .map(n => (n ?? '').trim().split(/\s+/)[0])
    .filter(Boolean);
  if (first.length <= 1) return first[0] ?? '';
  if (first.length === 2) return `${first[0]} & ${first[1]}`;
  return `${first.slice(0, -1).join(', ')} & ${first[first.length - 1]}`;
}

type Phase = 'loading' | 'invalid' | 'ready' | 'redeeming' | 'done' | 'error';

interface PreviewProfile {
  id: string;
  name?: string | null;
  thumbnails?: Array<{ url: string }> | null;
}
interface Preview {
  valid: boolean;
  source?: string | null;
  invitedByName?: string | null;
  profiles: PreviewProfile[];
}

/**
 * Magic-link landing page (route: /#invite?token=…).
 *
 * NOTE: this app has no global <ApolloProvider> — the shared Apollo
 * `client` is imported and called directly (client.query / client.mutate),
 * the same pattern the react-query api hooks use. Using Apollo's
 * useQuery/useMutation here throws "no client in context" (error 58).
 */
export default function InvitePage(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  // Token from the URL, falling back to the stash we set before bouncing
  // through phone login — so the invite survives the /auth round-trip
  // (the long token in the hash URL didn't reliably).
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (
      params.get('token') ||
      (typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('pendingInviteToken')
        : '') ||
      ''
    );
  }, [location.search]);

  const [phase, setPhase] = useState<Phase>('loading');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [linkedIds, setLinkedIds] = useState<string[]>([]);
  const redeemStarted = useRef(false);

  // 1. Preview the invite (public query).
  useEffect(() => {
    let alive = true;
    if (!token) {
      setPhase('invalid');
      return;
    }
    (async () => {
      try {
        const { data } = await client.query({
          query: INVITE_PREVIEW,
          variables: { token },
          fetchPolicy: 'network-only',
        });
        if (!alive) return;
        const p = data?.invitePreview as Preview | undefined;
        if (!p?.valid) {
          // Clear any stale stash so a dead token can't loop us back here
          // after a future login.
          sessionStorage.removeItem('pendingInviteToken');
          setPhase('invalid');
          return;
        }
        setPreview(p);
        setPhase('ready');
      } catch (err) {
        if (!alive) return;
        setErrorMsg(err instanceof Error ? err.message : 'Could not load the invite.');
        setPhase('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // 2. Auto-redeem once the preview is valid — the link is one-tap.
  useEffect(() => {
    if (phase !== 'ready' || redeemStarted.current) return;
    redeemStarted.current = true;
    void doRedeem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const doRedeem = async () => {
    setPhase('redeeming');
    setErrorMsg(null);
    try {
      // Signed in already — including the case where we've just come BACK
      // here right after phone login. Link the invite's kids to this
      // account and join the guardian group. This is the path that fixes
      // "logged in but no profiles": redemption now actually happens after
      // login instead of being dropped.
      if (auth.currentUser) {
        const { data } = await client.mutate({
          mutation: REDEEM_INVITE_FOR_CURRENT_USER,
          variables: { token },
        });
        const r = data?.redeemInviteForCurrentUser;
        if (r?.success) {
          sessionStorage.removeItem('pendingInviteToken');
          setLinkedIds(r.linkedProfileIds ?? []);
          setPhase('done');
          return;
        }
        setErrorMsg(r?.message ?? 'Could not link the invite.');
        setPhase('error');
        return;
      }

      // Not signed in — try zero-tap auto-login. This only completes when
      // the invite carries a phone AND the custom-token IAM grant is in
      // place; ANY failure (needs-phone-auth, missing grant, thrown error)
      // falls through to phone auth below.
      try {
        const { data } = await client.mutate({
          mutation: REDEEM_INVITE_AUTOLOGIN,
          variables: { token },
        });
        const r = data?.redeemInviteAutoLogin;
        if (r?.success && r.customToken) {
          await signInWithCustomToken(auth, r.customToken);
          await new Promise<void>(resolve => {
            const unsub = onAuthStateChanged(auth, u => {
              if (u) {
                unsub();
                resolve();
              }
            });
          });
          await client.resetStore().catch(() => undefined);
          sessionStorage.removeItem('pendingInviteToken');
          setLinkedIds(r.linkedProfileIds ?? []);
          setPhase('done');
          return;
        }
      } catch {
        // Auto-login threw (e.g. custom-token grant missing) — fall through
        // to phone auth; we never want to dead-end the recipient here.
      }

      // Remember the invite across the login redirect, then send them to
      // phone auth. `decidePostAuthRoute` routes back to /invite once
      // they're authenticated, and the signed-in branch above finishes the
      // link + shows the app-download screen.
      sessionStorage.setItem('pendingInviteToken', token);
      navigate('/auth');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
      setPhase('error');
    }
  };

  const retry = () => {
    redeemStarted.current = false;
    setPhase('ready');
  };

  const linkedNames = (() => {
    const byId = new Map((preview?.profiles ?? []).map(p => [p.id, p.name ?? '']));
    const named = linkedIds.map(id => byId.get(id)).filter((n): n is string => !!n);
    // Fall back to all invited kids' names if the id→name map missed (keeps
    // the message populated rather than dropping to the generic line).
    return named.length > 0
      ? named
      : (preview?.profiles ?? []).map(p => p.name ?? '').filter(Boolean);
  })();

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isCoparent = preview?.source === 'COPARENT';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F8FAFC',
        px: 3,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: '#fff',
          borderRadius: 4,
          p: 4,
          boxShadow: '0 10px 40px rgba(15,23,42,0.08)',
          textAlign: 'center',
        }}
      >
        {(phase === 'loading' || phase === 'redeeming') && (
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography color="text.secondary">
              {phase === 'redeeming' ? 'Setting up your account…' : 'Loading your invite…'}
            </Typography>
          </Stack>
        )}

        {phase === 'invalid' && (
          <Stack spacing={2}>
            <Typography variant="h5" fontWeight={800}>
              This invite has expired
            </Typography>
            <Typography color="text.secondary">
              Ask whoever sent it to share a fresh link, or set up from the
              GoodShowHQ desktop app.
            </Typography>
          </Stack>
        )}

        {phase === 'error' && (
          <Stack spacing={2}>
            <Typography variant="h5" fontWeight={800}>
              Couldn't finish
            </Typography>
            <Typography color="text.secondary">{errorMsg}</Typography>
            <Button variant="contained" onClick={retry}>
              Try again
            </Button>
          </Stack>
        )}

        {phase === 'done' && (
          <Stack spacing={2.5} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#DCFCE7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
              }}
            >
              🎉
            </Box>
            <Typography variant="h5" fontWeight={800}>
              You're all set
            </Typography>
            <Typography color="text.secondary">
              {linkedNames.length > 0
                ? `${joinNames(linkedNames)} ${linkedNames.length === 1 ? 'is' : 'are'} now linked to your account.`
                : 'Your account is ready.'}
            </Typography>
            <Box
              sx={{
                width: '100%',
                mt: 0.5,
                p: 2.5,
                borderRadius: 3,
                bgcolor: '#F1F5F9',
                textAlign: 'center',
              }}
            >
              {APP_ON_APP_STORE ? (
                <Stack spacing={1.5} alignItems="center">
                  <Typography variant="h6" fontWeight={800}>
                    Get the GoodShowHQ app
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Everything happens in the app — grab it to review and guide
                    your kids’ YouTube.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    href={APP_STORE_URL}
                    sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 700 }}
                  >
                    Download on the App Store
                  </Button>
                </Stack>
              ) : isIOS ? (
                <Stack spacing={1.5} alignItems="center">
                  <Typography variant="h6" fontWeight={800}>
                    Get the GoodShowHQ app
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    We’re in early access on TestFlight.
                  </Typography>
                  {TESTFLIGHT_URL ? (
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      href={TESTFLIGHT_URL}
                      sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 700 }}
                    >
                      Join the TestFlight beta
                    </Button>
                  ) : (
                    <Stack spacing={1} sx={{ width: '100%', textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary">
                        1. Install{' '}
                        <Button
                          href="https://apps.apple.com/app/testflight/id899247664"
                          sx={{ p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}
                        >
                          TestFlight
                        </Button>{' '}
                        from the App Store
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        2. Open the TestFlight invite we’ll email you, or ask the
                        person who invited you for the beta link
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        3. Install GoodShowHQ from TestFlight
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              ) : (
                <Stack spacing={1.5} alignItems="center">
                  <Typography variant="h6" fontWeight={800}>
                    Get the GoodShowHQ app
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    GoodShowHQ is an iOS app — open this link on your iPhone to
                    install it.
                  </Typography>
                </Stack>
              )}
            </Box>
            <Button onClick={() => navigate('/profile')} sx={{ textTransform: 'none' }}>
              Continue in browser
            </Button>
          </Stack>
        )}

        {preview?.valid && (phase === 'ready' || phase === 'redeeming') && (
          <Stack spacing={2} alignItems="center" sx={{ mt: phase === 'redeeming' ? 3 : 0 }}>
            <Typography variant="h5" fontWeight={800}>
              {isCoparent
                ? `${preview.invitedByName ?? 'A co-parent'} invited you`
                : 'Finish setting up'}
            </Typography>
            <Typography color="text.secondary">
              {isCoparent
                ? 'You’ll be able to review and guide these kids’ YouTube together.'
                : 'Your child’s profile is ready to link to your account.'}
            </Typography>
            <Stack direction="row" spacing={-1} sx={{ mt: 1 }}>
              {(preview.profiles ?? []).slice(0, 5).map(p => (
                <Avatar
                  key={p.id}
                  src={p.thumbnails?.[0]?.url}
                  alt={p.name ?? ''}
                  sx={{ width: 44, height: 44, border: '2px solid #fff' }}
                >
                  {(p.name ?? '?').charAt(0)}
                </Avatar>
              ))}
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
